import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Form, Input, Select } from 'antd';
import { Button, TextArea } from '@prio365/prio365-react-library';
import { makePrioStyles } from '../../../theme/utils';
import {
  MonthlyCloseId,
  TimeEntryMethod,
  TimeKeepingDayId,
  TimeKeepingDayType,
} from '../../../models/Types';
import { useSelector } from 'react-redux';
import {
  RootReducerState,
  getSettingsByKey,
  getTimeKeepingDayMe,
} from '../../../apps/main/rootReducer';
import Flex from '../../../components/Flex';
import { useForm } from 'antd/lib/form/Form';
import { TimeAndLeaveManagementTimelineItem } from '../../timeAndLeaveManagement/selectors';
import {
  TimeKeepingDay,
  TimeKeepingGeneral,
  TimeKeepingPrecision,
} from '../../../models/TimeKeeping';
import equals from 'deep-equal';
import { Setting } from '../../../models/Settings';

const TYPE_OPTIONS: TimeKeepingDayType[] = [
  'office',
  'homeOffice',
  'outOfOffice',
  'other',
];

const TIME_ENTRY_METHODS: TimeEntryMethod[] = [
  'terminal',
  'manual',
  'geofencing',
  'recording',
];
const TIME_ENTRY_METHOD_OBJECT = Object.fromEntries(
  TIME_ENTRY_METHODS.map((method) => [method.toUpperCase(), method])
) as Record<Uppercase<TimeEntryMethod>, TimeEntryMethod>;

const TIME_KEEPING_PRECISION_OPTIONS: TimeKeepingPrecision[] = [
  'fifteenMinutes',
  'tenMinutes',
  'fiveMinutes',
  'minute',
];

const TIME_KEEPING_PRECISION_OPTIONS_OBJECT = Object.fromEntries(
  TIME_KEEPING_PRECISION_OPTIONS.map((precision) => [
    precision.toUpperCase(),
    precision,
  ])
) as Record<Uppercase<TimeKeepingPrecision>, TimeKeepingPrecision>;

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);

const getMinuteOptions = (precisionType?: TimeKeepingPrecision): number[] => {
  switch (precisionType) {
    case TIME_KEEPING_PRECISION_OPTIONS_OBJECT.FIFTEENMINUTES:
      return [0, 15, 30, 45];
    case TIME_KEEPING_PRECISION_OPTIONS_OBJECT.TENMINUTES:
      return Array.from({ length: 6 }, (_, i) => i * 10);
    case TIME_KEEPING_PRECISION_OPTIONS_OBJECT.FIVEMINUTES:
      return Array.from({ length: 12 }, (_, i) => i * 5);
    case TIME_KEEPING_PRECISION_OPTIONS_OBJECT.MINUTE:
    default:
      return Array.from({ length: 60 }, (_, i) => i);
  }
};

const createTimeOptions = (values: number[]) =>
  values.map((option) => ({
    label: option.toString().padStart(2, '0'),
    value: option,
  }));

const getTimeComponent = (
  dateString: string | undefined,
  component: 'hours' | 'minutes'
): number | undefined => {
  if (!dateString) return undefined;
  const date = new Date(dateString);
  return component === 'hours' ? date.getHours() : date.getMinutes();
};

const getStartTime = (entries: any[] | undefined): string | undefined =>
  entries?.[0]?.startTime;

const getEndTime = (entries: any[] | undefined): string | undefined =>
  entries?.[entries?.length - 1]?.endTime;

const getBreakDurations = (
  entries: any[] | undefined
): Array<{ hours: number; minutes: number }> => {
  if (!entries || entries.length < 2) return [];
  const breaks: Array<{ hours: number; minutes: number }> = [];
  for (let i = 0; i < entries.length - 1; i++) {
    const end = new Date(entries[i].endTime).getTime();
    const start = new Date(entries[i + 1].startTime).getTime();
    const diffMinutes = Math.floor((start - end) / 60000);
    if (diffMinutes <= 0) {
      breaks.push({ hours: 0, minutes: 0 });
    } else {
      breaks.push({
        hours: Math.floor(diffMinutes / 60),
        minutes: diffMinutes % 60,
      });
    }
  }
  return breaks;
};

/**
 * Gives the common time entry method of all entries.
 * When all entries have the same time entry method, return that method.
 * Otherwise, return 'manual'.
 * @param entries
 * @returns
 */
const getTimeEntryMethod = (entries: any[] | undefined): TimeEntryMethod => {
  if (!entries || entries.length === 0) return TIME_ENTRY_METHOD_OBJECT.MANUAL;

  const isTerminal = entries.every(
    (entry) =>
      entry.startTimeEntryMethod === TIME_ENTRY_METHOD_OBJECT.TERMINAL &&
      entry.endTimeEntryMethod === TIME_ENTRY_METHOD_OBJECT.TERMINAL
  );
  if (isTerminal) return TIME_ENTRY_METHOD_OBJECT.TERMINAL;

  const isRecording = entries.every(
    (entry) =>
      entry.startTimeEntryMethod === TIME_ENTRY_METHOD_OBJECT.RECORDING &&
      entry.endTimeEntryMethod === TIME_ENTRY_METHOD_OBJECT.RECORDING
  );
  if (isRecording) return TIME_ENTRY_METHOD_OBJECT.RECORDING;

  const isGeofencing = entries.every(
    (entry) =>
      entry.startTimeEntryMethod === TIME_ENTRY_METHOD_OBJECT.GEOFENCING &&
      entry.endTimeEntryMethod === TIME_ENTRY_METHOD_OBJECT.GEOFENCING
  );
  if (isGeofencing) return TIME_ENTRY_METHOD_OBJECT.GEOFENCING;

  return TIME_ENTRY_METHOD_OBJECT.MANUAL;
};

const getTimeEntryMethodLabel = (
  method: TimeEntryMethod,
  t: (key: string) => string
): string => {
  return t(
    `hr:timeAndLeaveManagement.timekeepingDaysTable.timeEntryMethod.${method}`
  );
};

const useStyles = makePrioStyles((theme) => ({
  root: {},
  fullWidth: {
    width: '100%',
    minWidth: 150,
  },
  danger: {
    color: theme.old.palette.chromaticPalette.red,
    '&:hover': {
      backgroundColor: theme.old.palette.chromaticPalette.red,
      color: theme.old.palette.chromaticPalette.white + '!important',
      '& > .prio-button-icon': {
        color: theme.old.palette.chromaticPalette.white,
      },
    },
    '& > .prio-button-icon': {
      color: theme.old.palette.chromaticPalette.red,
    },
  },
  textAreaContainer: {
    width: '100%',
    position: 'relative',
    display: 'inline-block',
  },
  buttonTextArea: {
    position: 'absolute',
    top: '50%',
    padding: '8px',
    right: '12px',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    '& > svg': {
      display: 'flex',
    },
  },
}));

export interface TimeKeepingFormModel {
  type: TimeKeepingDayType;
  notes?: string;
  fromHour?: number;
  toHour?: number;
  fromMinute?: number;
  toMinute?: number;
  breaks?: Array<{ hours: number; minutes: number }>;
  timeEntryMethod?: string;
}

interface TimeKeepingEditFormProps {
  className?: string;
  selectedItem?: TimeAndLeaveManagementTimelineItem;
  onChange: (
    value: TimeKeepingFormModel,
    item?: TimeAndLeaveManagementTimelineItem
  ) => void;
  onDelete: (
    monthlyCloseId: MonthlyCloseId,
    timeKeepingDayId: TimeKeepingDayId
  ) => void;
  onDeleteBreak?: (index: number) => void;
  loading: boolean;
  timeKeepingGeneralSettings: TimeKeepingGeneral | null;
}

export const TimeKeepingEditForm: React.FC<TimeKeepingEditFormProps> = (
  props
) => {
  //#region ------------------------------ Defaults
  const {
    className,
    selectedItem,
    loading,
    timeKeepingGeneralSettings,
    onChange,
    onDelete,
    onDeleteBreak,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const [form] = useForm();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const timeKeepingDay = useSelector<RootReducerState, TimeKeepingDay>(
    (state) => getTimeKeepingDayMe(state, selectedItem?.id)
  );

  const timeKeepingPrecisionSetting = useSelector<RootReducerState, Setting[]>(
    (state) => getSettingsByKey(state, 'TimeKeeping_Precision')
  );
  const timeKeepingPrecision = useMemo(() => {
    const jsonValue = JSON.parse(timeKeepingPrecisionSetting?.[0]?.jsonValue);
    return (jsonValue?.precision ??
      jsonValue?.Precision) as TimeKeepingPrecision;
  }, [timeKeepingPrecisionSetting]);

  const showBreakFields =
    (timeKeepingDay?.timeKeepingEntries?.length ?? 0) >= 2;

  const initialValue: TimeKeepingFormModel = useMemo(() => {
    const startTime = getStartTime(timeKeepingDay?.timeKeepingEntries);
    const endTime = getEndTime(timeKeepingDay?.timeKeepingEntries);
    const breaks = getBreakDurations(timeKeepingDay?.timeKeepingEntries);

    return {
      type: timeKeepingDay?.type ?? 'office',
      notes: timeKeepingDay?.notes ?? '',
      fromHour: getTimeComponent(startTime, 'hours'),
      fromMinute: getTimeComponent(startTime, 'minutes'),
      toHour: getTimeComponent(endTime, 'hours'),
      toMinute: getTimeComponent(endTime, 'minutes'),
      breaks,
      timeEntryMethod: getTimeEntryMethodLabel(
        getTimeEntryMethod(timeKeepingDay?.timeKeepingEntries),
        t
      ),
    };
  }, [timeKeepingDay, t]);

  const [updateTextAreaString, setUpdateTextAreaString] =
    useState<string>(undefined);
  const [isTextAreaUpdated, setIsTextAreaUpdated] = useState<boolean>(false);

  const enableOfficeType = useMemo(() => {
    if (timeKeepingGeneralSettings) {
      const editPolicy =
        timeKeepingGeneralSettings.editPolicy ??
        timeKeepingGeneralSettings.EditPolicy;
      if (editPolicy) {
        return (
          editPolicy === 'editsAreAllowed' ||
          editPolicy === 'editsRequireApproval'
        );
      }
    }
    return true;
  }, [timeKeepingGeneralSettings]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const onHandleValuesChange: (
    changedValues: TimeKeepingFormModel,
    allValues: TimeKeepingFormModel
  ) => void = (_, allValues) => {
    const allValuesButNotes: TimeKeepingFormModel = {
      ...allValues,
      notes: initialValue?.notes,
    };
    if (selectedItem) {
      // Use Redux entries so item.entries always matches the breaks count shown in the form.
      const itemWithCurrentEntries = timeKeepingDay
        ? { ...selectedItem, entries: timeKeepingDay.timeKeepingEntries }
        : selectedItem;
      onChange(allValuesButNotes, itemWithCurrentEntries);
    }
  };

  const handleOnDescriptionChange = (value) => {
    setIsTextAreaUpdated(value !== initialValue?.notes);
    setUpdateTextAreaString(value);
  };

  const handleSaveDescription = () => {
    const currentValues = {
      type: form.getFieldsValue().type,
      notes: updateTextAreaString,
    };
    const asyncOnChange = async () => {
      await onChange(currentValues, selectedItem);
    };

    asyncOnChange().then(() => {
      setIsTextAreaUpdated(false);
      setUpdateTextAreaString(undefined);
    });
  };
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    if (!equals(form.getFieldsValue(), initialValue)) {
      form.setFieldsValue(initialValue);
    }
  }, [form, initialValue]);
  //#endregion

  return (
    <Form<TimeKeepingFormModel>
      className={classNames(classes.root, className)}
      initialValues={initialValue}
      onValuesChange={(changedValues, allValues) => {
        const hasTimeOrTypeChange =
          changedValues.type ||
          changedValues.fromHour !== undefined ||
          changedValues.fromMinute !== undefined ||
          changedValues.toHour !== undefined ||
          changedValues.toMinute !== undefined ||
          changedValues.breaks !== undefined;

        if (hasTimeOrTypeChange) {
          onHandleValuesChange(changedValues, allValues);
        }
      }}
      form={form}
      layout="vertical"
    >
      <Flex.Column>
        <Flex.Row childrenGap={16}>
          <Flex.Item flex={1}>
            <Form.Item
              name="type"
              label={t('timeKeeping:form.labels.type')}
              style={{ width: '100%' }}
            >
              <Select
                options={TYPE_OPTIONS.map((type) => ({
                  label: t(`timeKeeping:form.values.type.${type}`),
                  value: type,
                  disabled: type === 'office' ? !enableOfficeType : false,
                }))}
                disabled={
                  loading ||
                  (initialValue?.type === 'office' && !enableOfficeType)
                }
              />
            </Form.Item>
          </Flex.Item>
          <Flex.Row flex={1}>
            <Flex.Item flex={1}>
              <Form.Item
                name="timeEntryMethod"
                label={t('timeKeeping:form.labels.timeEntryMethod')}
                style={{ width: '100%' }}
              >
                <Input disabled={true} />
              </Form.Item>
            </Flex.Item>
          </Flex.Row>
        </Flex.Row>
        <Flex.Row childrenGap={16}>
          <Flex.Row flex={1} childrenGap={8}>
            <Flex.Item flex={1}>
              <Form.Item
                name="fromHour"
                label={t('timeKeeping:form.labels.from')}
                style={{ width: '100%' }}
              >
                <Select
                  options={createTimeOptions(HOUR_OPTIONS)}
                  disabled={loading}
                />
              </Form.Item>
            </Flex.Item>
            <Flex.Item flex={1}>
              <Form.Item
                name="fromMinute"
                style={{ width: '100%' }}
                label={<div></div>}
              >
                <Select
                  options={createTimeOptions(
                    getMinuteOptions(timeKeepingPrecision)
                  )}
                  disabled={loading}
                />
              </Form.Item>
            </Flex.Item>
          </Flex.Row>
          <Flex.Row flex={1} childrenGap={8}>
            <Flex.Item flex={1}>
              <Form.Item
                name="toHour"
                label={t('timeKeeping:form.labels.to')}
                style={{ width: '100%' }}
              >
                <Select
                  options={createTimeOptions(HOUR_OPTIONS)}
                  disabled={loading}
                />
              </Form.Item>
            </Flex.Item>
            <Flex.Item flex={1}>
              <Form.Item
                name="toMinute"
                style={{ width: '100%' }}
                label={<div></div>}
              >
                <Select
                  options={createTimeOptions(
                    getMinuteOptions(timeKeepingPrecision)
                  )}
                  disabled={loading}
                />
              </Form.Item>
            </Flex.Item>
          </Flex.Row>
        </Flex.Row>
        {showBreakFields && (
          <Form.List name="breaks">
            {(fields) => {
              const rows: (typeof fields)[] = [];
              for (let i = 0; i < fields.length; i += 2) {
                rows.push(fields.slice(i, i + 2));
              }
              return rows.map((rowFields) => (
                <Flex.Row key={rowFields[0].key} childrenGap={16}>
                  {rowFields.map((field) => (
                    <Flex.Row key={field.key} flex={1} childrenGap={8}>
                      <Flex.Item flex={1}>
                        <Form.Item
                          name={[field.name, 'hours']}
                          label={`${t('timeKeeping:form.labels.break')} ${field.name + 1}`}
                          style={{ width: '100%' }}
                        >
                          <Select
                            options={createTimeOptions(HOUR_OPTIONS)}
                            disabled={loading}
                          />
                        </Form.Item>
                      </Flex.Item>
                      <Flex.Item flex={1}>
                        <Form.Item
                          label={<div></div>}
                          style={{ width: '100%' }}
                        >
                          <Flex.Row childrenGap={8}>
                            <Flex.Item flex={1}>
                              <Form.Item
                                name={[field.name, 'minutes']}
                                style={{ width: '100%', marginBottom: 0 }}
                                noStyle
                              >
                                <Select
                                  options={createTimeOptions(
                                    getMinuteOptions(timeKeepingPrecision)
                                  )}
                                  disabled={loading}
                                />
                              </Form.Item>
                            </Flex.Item>
                            <Flex.Item>
                              <Button
                                iconProp={['fal', 'trash']}
                                type="danger-link"
                                disabled={loading}
                                onClick={() => onDeleteBreak?.(field.name)}
                              />
                            </Flex.Item>
                          </Flex.Row>
                        </Form.Item>
                      </Flex.Item>
                    </Flex.Row>
                  ))}
                  {rowFields.length === 1 && <Flex.Row flex={1} />}
                </Flex.Row>
              ));
            }}
          </Form.List>
        )}
        <Flex.Row flex={1}>
          <Flex.Item flex={2}>
            <Form.Item
              name="notes"
              label={t('timeKeeping:form.labels.description')}
            >
              <div className={classes.textAreaContainer}>
                <TextArea
                  autoSize={{ minRows: 2, maxRows: 5 }}
                  style={{
                    width: '100%',
                    paddingRight: `${isTextAreaUpdated ? '52px' : '0px'}`,
                  }}
                  value={updateTextAreaString ?? initialValue?.notes}
                  onChange={handleOnDescriptionChange}
                ></TextArea>
                {isTextAreaUpdated && (
                  <Button
                    loading={loading}
                    className={classes.buttonTextArea}
                    iconProp={['far', 'check']}
                    tooltip={t('timeKeeping:form.labels.tooltipSaveNotes')}
                    onClick={loading ? () => {} : handleSaveDescription}
                  ></Button>
                )}
              </div>
            </Form.Item>
          </Flex.Item>
        </Flex.Row>
        <Flex.Row>
          <Button
            type="default"
            className={classes.danger}
            disabled={loading}
            loading={loading}
            onClick={() => {
              if (timeKeepingDay) {
                onDelete(
                  timeKeepingDay?.monthlyCloseId,
                  timeKeepingDay?.timeKeepingDayId
                );
              }
            }}
          >
            {t('common:actions.delete')}
          </Button>
        </Flex.Row>
      </Flex.Column>
    </Form>
  );
};

export default TimeKeepingEditForm;
